import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { ContactService } from "../../services/contact.service";
import { TelemetryService } from "../../services/telemetry.service";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { s25Resource } from "../../util/s25.resource";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { DropDownItem } from "../../pojo/DropDownItem";
import { DropDownPaginatedModel } from "../../pojo/DropDownPaginatedModel";
import { CopySeriesDatabaseProcParams } from "./copy.series.database.service";
import { SeDemoDataResetReport, SeDemoDataService } from "./se.demo.data.service";

@TypeManagerDecorator("s25-ng-se-demo-data")
@Component({
    selector: "s25-ng-se-demo-data",
    host: { class: "se-demo-data" },
    template: `
        @if (username.value() === "service25" && bootstrap.value()?.allowed) {
            <p class="ngBold c-margin-top--single">Connect25 demo data</p>
            <p class="ngFinePrint c-margin-bottom--single">
                Clone copies Connect25 configuration onto this instance. Reset only deletes demo activity created in a
                time window — it does not wipe the instance. Both actions run only on allowlisted internal instances.
            </p>

            <p class="ngBold c-margin-bottom--quarter">Clone onto this instance</p>
            <label class="ngBlock c-margin-bottom--quarter" for="seDemoTargetInstance">Target instance</label>
            <input
                id="seDemoTargetInstance"
                class="c-input c-margin-bottom--single"
                [value]="sessionInstanceId()"
                readonly
                disabled
            />

            <label class="ngBlock c-margin-bottom--quarter">Source instance</label>
            @if (sourceOptions().length) {
                <s25-dropdown-paginated
                    class="se-demo-data-source-dropdown c-margin-bottom--single"
                    [model]="sourceDropdownModel"
                    [chosen]="chosenSource()"
                    [onSelect]="onSourceSelect"
                    [readOnly]="cloneInProgress()"
                    [choice]="sourceChoice"
                    ariaLabel="Source instance"
                />
                <ng-template #sourceChoice let-item="item">
                    <span [innerHTML]="item.itemName"></span>
                </ng-template>
            } @else {
                <p class="ngFinePrint c-margin-bottom--single">No source instances available.</p>
            }

            <button
                class="aw-button aw-button--primary c-margin-bottom--single"
                [disabled]="!canOpenCloneWarning()"
                (click)="openCloneWarning()"
            >
                Clone Connect25 data
            </button>

            <p class="ngBold c-margin-bottom--quarter">Reset demo activity</p>
            <p class="ngFinePrint c-margin-bottom--single">
                Set a start and end time. Only demo activity created on this instance in that range is removed. Defaults
                to the last 12 clock hours through now. End is exclusive, so the default is one minute past the current
                minute. The window cannot span more than 12 clock hours.
            </p>
            <label class="ngBlock c-margin-bottom--quarter" for="seDemoResetStart">Window start</label>
            <input
                id="seDemoResetStart"
                class="c-input c-margin-bottom--single"
                type="datetime-local"
                [ngModel]="startDt()"
                (ngModelChange)="startDt.set($event)"
                [disabled]="resetInProgress()"
            />
            <label class="ngBlock c-margin-bottom--quarter" for="seDemoResetEnd">Window end</label>
            <input
                id="seDemoResetEnd"
                class="c-input c-margin-bottom--single"
                type="datetime-local"
                [ngModel]="endDt()"
                (ngModelChange)="endDt.set($event)"
                [disabled]="resetInProgress()"
            />
            <button
                class="aw-button aw-button--outline c-margin-bottom--single"
                [disabled]="!canSubmitPreview()"
                (click)="runPreview()"
            >
                Preview reset
            </button>
            <button
                class="aw-button aw-button--danger c-margin-bottom--single"
                [disabled]="!canOpenApplyWarning()"
                (click)="openApplyWarning()"
            >
                Apply reset
            </button>

            @if (resetReport()) {
                <div class="c-margin-bottom--single">
                    @for (row of resetReport()?.summary || []; track row.actionKind) {
                        <p class="ngFinePrint">{{ row.actionKind }}: {{ row.rowCount }}</p>
                    }
                </div>
            }

            @if (statusMessage()) {
                <div
                    class="c-margin-bottom--single cn-alert"
                    [class.cn-alert--success]="statusTone() === 'success'"
                    [class.cn-alert--danger]="statusTone() === 'error'"
                    [class.cn-alert--warning]="statusTone() === 'warning'"
                    role="alert"
                >
                    {{ statusMessage() }}
                </div>
            }

            <s25-ng-modal
                #cloneWarningModal
                title="Overwrite Connect25 data on this instance"
                [type]="'continue'"
                [size]="'lg'"
            >
                <ng-template #s25ModalBody>
                    <p class="c-margin-bottom--single">
                        You are about to clone from <strong>{{ selectedSourceInstance() }}</strong> onto
                        <strong>{{ sessionInstanceId() }}</strong
                        >.
                    </p>
                    <p>This overwrites Connect25 configuration on the target. It cannot be undone.</p>
                </ng-template>
            </s25-ng-modal>

            <s25-ng-modal #applyWarningModal title="Apply demo reset" [type]="'continue'" [size]="'lg'">
                <ng-template #s25ModalBody>
                    <p>
                        This deletes demo activity in the selected window on {{ sessionInstanceId() }}. It does not
                        reset the whole instance or undo a clone.
                    </p>
                </ng-template>
            </s25-ng-modal>

            <s25-ng-modal #typeConfirmModal title="Type instance to confirm" [type]="'custom'" [size]="'md'">
                <ng-template #s25ModalBody>
                    <p class="c-margin-bottom--quarter">
                        Type <strong>{{ sessionInstanceId() }}</strong> to confirm.
                    </p>
                    <input
                        class="c-input"
                        type="text"
                        [ngModel]="typeConfirmText()"
                        (ngModelChange)="typeConfirmText.set($event)"
                    />
                </ng-template>
                <ng-template #s25ModalFooter>
                    <div class="buttons">
                        <button class="aw-button aw-button--outline" (click)="closeTypeConfirm(false)">Cancel</button>
                        <button
                            class="aw-button aw-button--danger"
                            [disabled]="!canSubmitTypeConfirm()"
                            (click)="closeTypeConfirm(true)"
                        >
                            Confirm
                        </button>
                    </div>
                </ng-template>
            </s25-ng-modal>
        }
    `,
    styles: `
        :host.se-demo-data {
            .c-input,
            .se-demo-data-source-dropdown {
                width: 60%;
                max-width: 400px;
            }
            .buttons {
                display: flex;
                gap: 0.5em;
                justify-content: flex-end;
                padding: 1em;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25SeDemoDataComponent {
    readonly cloneWarningModal = viewChild<S25ModalComponent<boolean>>("cloneWarningModal");
    readonly applyWarningModal = viewChild<S25ModalComponent<boolean>>("applyWarningModal");
    readonly typeConfirmModal = viewChild<S25ModalComponent<boolean>>("typeConfirmModal");

    readonly username = s25Resource({ loader: () => ContactService.getCurrentUsername() });
    readonly bootstrap = s25Resource({
        if: () => this.username.value() === "service25",
        loader: () => SeDemoDataService.getBootstrap(),
    });

    private readonly initialResetWindow = SeDemoDataService.defaultResetWindow();
    readonly startDt = signal(this.initialResetWindow.startDt);
    readonly endDt = signal(this.initialResetWindow.endDt);
    readonly typeConfirmText = signal("");
    readonly cloneInProgress = signal(false);
    readonly resetInProgress = signal(false);
    readonly statusMessage = signal("");
    readonly statusTone = signal<"success" | "error" | "warning" | "info">("info");
    readonly resetReport = signal<SeDemoDataResetReport | undefined>(undefined);
    readonly chosenSource = signal<DropDownItem | undefined>(undefined);

    readonly sessionInstanceId = computed(() => this.bootstrap.value()?.sessionInstanceId || "");
    readonly sourceOptions = computed(() => this.bootstrap.value()?.sources || []);

    readonly selectedSourceInstance = computed(() => (this.chosenSource()?.val as string) || "");

    readonly canOpenCloneWarning = computed(
        () => !!this.selectedSourceInstance() && this.sourceOptions().length > 0 && !this.cloneInProgress(),
    );

    readonly resetWindowHours = computed(() => SeDemoDataService.datediffHours(this.startDt(), this.endDt()));

    readonly canSubmitPreview = computed(
        () => this.resetWindowHours() !== null && (this.resetWindowHours() as number) <= 12 && !this.resetInProgress(),
    );

    readonly canOpenApplyWarning = computed(() => this.canSubmitPreview());

    readonly canSubmitTypeConfirm = computed(() => this.typeConfirmText().trim() === this.sessionInstanceId());

    readonly sourceDropdownModel: DropDownPaginatedModel = {
        items: [],
        itemNameProp: "itemName",
        placeholder: "Select source instance",
        emptyText: "No matching source instances",
        searchEnabled: true,
        pageSize: 10_000,
        noItemIdForGet: true,
    };

    constructor() {
        effect(() => {
            this.sourceDropdownModel.items = this.sourceOptions().map((option) => this.toDropdownItem(option));
        });
    }

    readonly onSourceSelect = (item?: DropDownItem) => {
        this.chosenSource.set(item);
        this.typeConfirmText.set("");
        this.statusMessage.set("");
    };

    optionLabel(option: CopySeriesDatabaseProcParams): string {
        if (option.targetDb && option.targetDb !== option.targetInstanceId) {
            return `${option.targetInstanceId} (${option.targetDb})`;
        }
        return option.targetInstanceId || "";
    }

    async openCloneWarning() {
        if (!this.canOpenCloneWarning()) {
            return;
        }
        const continued = await this.cloneWarningModal()?.open();
        if (!continued) {
            return;
        }
        this.typeConfirmText.set("");
        const confirmed = await this.typeConfirmModal()?.open();
        if (!confirmed) {
            this.typeConfirmText.set("");
            return;
        }
        await this.runClone();
    }

    async openApplyWarning() {
        if (!this.canOpenApplyWarning()) {
            return;
        }
        const continued = await this.applyWarningModal()?.open();
        if (!continued) {
            return;
        }
        this.typeConfirmText.set("");
        const confirmed = await this.typeConfirmModal()?.open();
        if (!confirmed) {
            this.typeConfirmText.set("");
            return;
        }
        await this.runReset(true);
    }

    closeTypeConfirm(confirmed: boolean) {
        this.typeConfirmModal()?.close(confirmed);
    }

    async runPreview() {
        if (!this.canSubmitPreview()) {
            return;
        }
        await this.runReset(false);
    }

    private toDropdownItem(option: CopySeriesDatabaseProcParams): DropDownItem {
        return {
            val: option.targetInstanceId,
            txt: this.optionLabel(option),
            itemId: option.targetInstanceId,
            itemName: this.optionLabel(option),
        };
    }

    private async runClone() {
        this.cloneInProgress.set(true);
        this.statusTone.set("info");
        this.statusMessage.set("Cloning Connect25 data...");
        TelemetryService.send("ServicePortal", "CloneStart");
        try {
            const message = await SeDemoDataService.clone(this.selectedSourceInstance());
            this.cloneInProgress.set(false);
            this.statusTone.set("success");
            this.statusMessage.set(message);
        } catch (caughtError) {
            this.cloneInProgress.set(false);
            this.statusTone.set(caughtError?.status === 409 ? "warning" : "error");
            this.statusMessage.set(SeDemoDataService.formatError(caughtError));
        }
    }

    private async runReset(apply: boolean) {
        this.resetInProgress.set(true);
        this.statusTone.set("info");
        this.statusMessage.set(apply ? "Applying demo reset..." : "Previewing demo reset...");
        TelemetryService.send("ServicePortal", apply ? "ResetApply" : "ResetPreview");
        try {
            const report = await SeDemoDataService.resetDemo(this.startDt(), this.endDt(), apply);
            this.resetReport.set(report);
            this.resetInProgress.set(false);
            this.statusTone.set("success");
            this.statusMessage.set(report.message || (apply ? "Demo reset applied." : "Demo reset preview."));
        } catch (caughtError) {
            this.resetInProgress.set(false);
            this.statusTone.set(caughtError?.status === 409 ? "warning" : "error");
            this.statusMessage.set(SeDemoDataService.formatError(caughtError));
        }
    }
}
